import { useState, useEffect, useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  Collapse,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Slider,
  TextField,
  Typography,
} from "@mui/material";
import {
  Save as SaveIcon,
  DeleteOutline as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  TuneOutlined as SettingsIcon,
  RecordVoiceOver as VoiceIcon,
  RuleOutlined as RulesIcon,
  Preview as PreviewIcon,
  Add as AddIcon,
  MessageOutlined as PromptIcon,
} from "@mui/icons-material";
import MyMagicPanelShell from "../shared/MyMagicPanelShell";

/* ── constants ── */

const FORMALITY_MARKS = [
  { value: 1, label: "Casual" },
  { value: 3, label: "Balanced" },
  { value: 5, label: "Formal" },
];

const WRITING_TONE_OPTIONS = [
  { key: "direct_warm", label: "Direct & Warm" },
  { key: "polished_formal", label: "Polished & Formal" },
  { key: "casual_playful", label: "Casual & Playful" },
];
const TEXT_TONE_OPTIONS = [
  { key: "text_friendly", label: "Friendly" },
  { key: "text_professional", label: "Professional" },
  { key: "text_urgent", label: "Urgent" },
  { key: "text_playful", label: "Playful" },
];

const LENGTH_LABELS = {
  one_two_sentences: "One or two sentences",
  short_paragraph: "A short paragraph",
  few_paragraphs: "A few paragraphs",
};

const TONE_LABELS = {
  direct_warm: "Direct and warm",
  polished_formal: "Polished and formal",
  casual_playful: "Casual and playful",
  text_friendly: "Friendly",
  text_professional: "Professional",
  text_urgent: "Urgent",
  text_playful: "Playful",
};

/* ── shared styles ── */

const radioSx = { fontSize: 12 };

const SectionLabel = ({ children }) => (
  <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary", mb: 0.5 }}>
    {children}
  </Typography>
);

/* ── collapsible card ── */

const TrainingCard = ({ title, icon, open, onToggle, action, children }) => (
  <Card variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
    <Box
      onClick={onToggle}
      sx={{
        display: "flex",
        alignItems: "center",
        px: 1.5,
        py: 0.75,
        cursor: "pointer",
        backgroundColor: "#FAFBFC",
        "&:hover": { backgroundColor: "#F3F4F6" },
        borderBottom: open ? "1px solid #E5E7EB" : "none",
      }}
    >
      {icon}
      <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#334155", ml: 0.75, flex: 1 }}>
        {title}
      </Typography>
      {action && <Box onClick={(e) => e.stopPropagation()}>{action}</Box>}
      {open ? <ExpandLessIcon sx={{ fontSize: 16, color: "#94a3b8" }} /> : <ExpandMoreIcon sx={{ fontSize: 16, color: "#94a3b8" }} />}
    </Box>
    <Collapse in={open}>
      <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>{children}</CardContent>
    </Collapse>
  </Card>
);

/* ── prompt assembly (preview) ── */

function assemblePromptPreview(draft, modeKind = "writing") {
  if (!draft) return "";
  const lines = [];
  lines.push(
    modeKind === "text"
      ? "You are drafting a text message on behalf of the user."
      : "You are drafting an email on behalf of the user."
  );
  lines.push("ONLY use facts provided in the contact context. Do NOT invent details.");
  lines.push("");
  lines.push("USER'S TRAINED WRITING STYLE:");
  if (draft.formality) lines.push(`- Formality level: ${draft.formality}/5`);
  if (draft.contractions) lines.push(`- Contractions: ${draft.contractions}`);
  if (draft.greeting) lines.push(`- Preferred greeting: ${draft.greeting}`);
  if (draft.signoff) lines.push(`- Preferred signoff: ${draft.signoff}`);
  if (draft.length) lines.push(`- Typical length: ${LENGTH_LABELS[draft.length] || draft.length}`);
  if (draft.emojis) lines.push(`- Emoji use: ${draft.emojis}`);
  if (draft.toneArchetype) lines.push(`- Tone: ${TONE_LABELS[draft.toneArchetype] || draft.toneArchetype}`);

  const samples = (draft.voiceSamples || []).filter((s) => s.trim());
  if (draft.voiceSample?.trim()) samples.unshift(draft.voiceSample.trim());

  if (samples.length > 0) {
    lines.push("");
    lines.push(`VOICE SAMPLES (${samples.length} example${samples.length > 1 ? "s" : ""} of how the user writes):`);
    samples.forEach((s, i) => {
      lines.push(`--- Sample ${i + 1} ---`);
      lines.push(`"${s}"`);
    });
    lines.push("");
    lines.push("MATCH THIS VOICE CLOSELY. Do not sound generic or AI-like.");
  }

  if (draft.customInstructions?.trim()) {
    lines.push("");
    lines.push("CUSTOM INSTRUCTIONS:");
    lines.push(draft.customInstructions);
  }

  return lines.join("\n");
}

/* ── main panel ── */

const VoiceProfileDetailPanel = ({ profile, modeKind = "writing", onSave, onDelete, onClose }) => {
  const [draft, setDraft] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [openCards, setOpenCards] = useState({ prompt: false, settings: false, voice: false, rules: false, preview: false });
  const [editedPreview, setEditedPreview] = useState("");

  useEffect(() => {
    if (profile) {
      setDraft({
        ...profile,
        defaultPrompt: profile.defaultPrompt || "",
        voiceSamples: profile.voiceSamples || [],
        customInstructions: profile.customInstructions || "",
      });
      setDirty(false);
    } else {
      setDraft(null);
      setDirty(false);
    }
  }, [profile]);

  const toggleCard = (key) => setOpenCards((prev) => ({ ...prev, [key]: !prev[key] }));

  const update = (field, value) => {
    setDirty(true);
    setDraft((p) => ({ ...p, [field]: value }));
  };

  const handleSave = () => {
    if (!draft?.name?.trim()) return;
    onSave({ ...draft, editedPromptPreview: editedPreview });
    setDirty(false);
  };

  const promptPreview = useMemo(() => assemblePromptPreview(draft, modeKind), [draft, modeKind]);
  const toneOptions = modeKind === "text" ? TEXT_TONE_OPTIONS : WRITING_TONE_OPTIONS;

  // Sync editedPreview when draft settings change (unless user has manually edited)
  useEffect(() => {
    setEditedPreview(promptPreview);
  }, [promptPreview]);

  // Voice samples helpers
  const addSample = () => {
    setDirty(true);
    setDraft((p) => ({ ...p, voiceSamples: [...(p.voiceSamples || []), ""] }));
  };
  const updateSample = (idx, value) => {
    setDirty(true);
    setDraft((p) => {
      const copy = [...(p.voiceSamples || [])];
      copy[idx] = value;
      return { ...p, voiceSamples: copy };
    });
  };
  const removeSample = (idx) => {
    setDirty(true);
    setDraft((p) => ({ ...p, voiceSamples: (p.voiceSamples || []).filter((_, i) => i !== idx) }));
  };

  const headerTitle = (
    <Typography sx={{ fontSize: 13, fontWeight: 600, color: "text.primary", flex: 1 }}>
      {draft?.name || "Mode Editor"}
    </Typography>
  );

  const headerActions = (
    <IconButton
      size="small"
      onClick={handleSave}
      title="Save"
      disabled={!dirty}
      sx={{
        backgroundColor: dirty ? "#16a34a" : "#E5E7EB",
        color: dirty ? "#FFFFFF" : "#9CA3AF",
        width: 28,
        height: 28,
        "&:hover": { backgroundColor: dirty ? "#15803d" : "#D1D5DB" },
        "&.Mui-disabled": { backgroundColor: "#E5E7EB", color: "#9CA3AF" },
      }}
    >
      <SaveIcon sx={{ fontSize: 16 }} />
    </IconButton>
  );

  if (!profile) {
    return (
      <MyMagicPanelShell onClose={onClose} titleContent={headerTitle}>
        <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
          <Typography sx={{ color: "text.secondary", fontSize: 13 }}>
            {modeKind === "text" ? "Select a text mode to train" : "Select a writing mode to train"}
          </Typography>
        </Box>
      </MyMagicPanelShell>
    );
  }

  if (!draft) return null;

  const sampleCount = (draft.voiceSamples || []).filter((s) => s.trim()).length + (draft.voiceSample?.trim() ? 1 : 0);

  return (
    <MyMagicPanelShell
      onClose={onClose}
      titleContent={headerTitle}
      headerActions={headerActions}
      footerRight={
        !draft.isStarter ? (
          <IconButton
            size="small"
            onClick={() => onDelete(draft)}
            title="Delete mode"
            sx={{ color: "#9CA3AF", "&:hover": { color: "#6B7280", backgroundColor: "#F3F4F6" } }}
          >
            <DeleteIcon sx={{ fontSize: 16 }} />
          </IconButton>
        ) : null
      }
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, pt: 0.5 }}>

        {/* ── 0. Default Prompt ── */}
        <TrainingCard
          title="Default Prompt"
          icon={<PromptIcon sx={{ fontSize: 16, color: "#F59E0B" }} />}
          open={openCards.prompt}
          onToggle={() => toggleCard("prompt")}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Typography sx={{ fontSize: 11, color: "#64748b", lineHeight: 1.4 }}>
              {modeKind === "text"
                ? "This prompt is pre-filled when you select this text mode in Compose. You can customize it per mode so it's ready to go."
                : "This prompt is pre-filled when you select this writing mode in Compose. You can customize it per mode so it's ready to go."}
            </Typography>
            <TextField
              size="small"
              fullWidth
              multiline
              rows={3}
              value={draft.defaultPrompt || ""}
              onChange={(e) => update("defaultPrompt", e.target.value)}
              placeholder={modeKind === "text"
                ? "e.g. Write a concise follow-up text asking for a quick reply..."
                : "e.g. Write a professional cold outreach email introducing myself and explaining how I can provide value..."}
              sx={{ "& .MuiInputBase-root": { fontSize: 12 } }}
            />
          </Box>
        </TrainingCard>

        {/* ── 1. Profile Settings ── */}
        <TrainingCard
          title="Profile Settings"
          icon={<SettingsIcon sx={{ fontSize: 16, color: "#3B82F6" }} />}
          open={openCards.settings}
          onToggle={() => toggleCard("settings")}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              label="Mode Name"
              size="small"
              fullWidth
              value={draft.name}
              onChange={(e) => update("name", e.target.value)}
              placeholder="e.g. Investor Outreach"
              InputLabelProps={{ shrink: true }}
                         />

            <Box>
              <SectionLabel>Formality</SectionLabel>
              <Box sx={{ px: 2.5 }}>
                <Slider
                  value={draft.formality}
                  onChange={(_, v) => update("formality", v)}
                  step={1} min={1} max={5}
                  marks={FORMALITY_MARKS}
                  size="small"
                  sx={{
                    "& .MuiSlider-markLabel": { fontSize: 11 },
                  }}
                />
              </Box>
            </Box>

            <Box>
              <SectionLabel>Tone</SectionLabel>
              <RadioGroup row value={draft.toneArchetype} onChange={(e) => update("toneArchetype", e.target.value)}>
                {toneOptions.map((t) => (
                  <FormControlLabel key={t.key} value={t.key} control={<Radio size="small" disabled={false} />} label={<Typography sx={radioSx}>{t.label}</Typography>} />
                ))}
              </RadioGroup>
            </Box>

            <Box>
              <SectionLabel>Contractions</SectionLabel>
              <RadioGroup row value={draft.contractions} onChange={(e) => update("contractions", e.target.value)}>
                {["Always", "Sometimes", "Rarely", "Never"].map((opt) => (
                  <FormControlLabel key={opt} value={opt} control={<Radio size="small" disabled={false} />} label={<Typography sx={radioSx}>{opt}</Typography>} />
                ))}
              </RadioGroup>
            </Box>

            <Box sx={{ display: "flex", gap: 1 }}>
              <TextField label="Greeting" size="small" fullWidth value={draft.greeting} onChange={(e) => update("greeting", e.target.value)} InputLabelProps={{ shrink: true }} disabled={false} />
              <TextField label="Sign-off" size="small" fullWidth value={draft.signoff} onChange={(e) => update("signoff", e.target.value)} InputLabelProps={{ shrink: true }} disabled={false} />
            </Box>

            <Box>
              <SectionLabel>{modeKind === "text" ? "Text Length" : "Email Length"}</SectionLabel>
              <RadioGroup row value={draft.length} onChange={(e) => update("length", e.target.value)}>
                {[
                  { value: "one_two_sentences", label: "1-2 sentences" },
                  { value: "short_paragraph", label: "Short paragraph" },
                  { value: "few_paragraphs", label: "Few paragraphs" },
                ].map((opt) => (
                  <FormControlLabel key={opt.value} value={opt.value} control={<Radio size="small" disabled={false} />} label={<Typography sx={radioSx}>{opt.label}</Typography>} />
                ))}
              </RadioGroup>
            </Box>

            <Box>
              <SectionLabel>Emojis</SectionLabel>
              <RadioGroup row value={draft.emojis} onChange={(e) => update("emojis", e.target.value)}>
                {["Never", "Rarely", "Sometimes", "Often"].map((opt) => (
                  <FormControlLabel key={opt} value={opt} control={<Radio size="small" disabled={false} />} label={<Typography sx={radioSx}>{opt}</Typography>} />
                ))}
              </RadioGroup>
            </Box>
          </Box>
        </TrainingCard>

        {/* ── 2. Voice Training ── */}
        <TrainingCard
          title={`Voice Training${sampleCount > 0 ? ` (${sampleCount})` : ""}`}
          icon={<VoiceIcon sx={{ fontSize: 16, color: "#7c3aed" }} />}
          open={openCards.voice}
          onToggle={() => toggleCard("voice")}
          action={
            <IconButton size="small" onClick={addSample} title="Add sample" sx={{ backgroundColor: "#16a34a", "&:hover": { backgroundColor: "#15803d" }, width: 20, height: 20, p: 0 }}>
              <AddIcon sx={{ fontSize: 14, color: "#FFFFFF" }} />
            </IconButton>
          }
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Typography sx={{ fontSize: 11, color: "#64748b", lineHeight: 1.4 }}>
              {modeKind === "text"
                ? "Paste text messages you've actually sent. The more samples you provide, the better the AI will match your texting style. Each sample teaches the model your word choices, sentence structure, and personality."
                : "Paste emails you've actually sent. The more samples you provide, the better the AI will match your natural writing voice. Each sample teaches the model your word choices, sentence structure, and personality."}
            </Typography>

            {/* Legacy single sample field */}
            {(draft.voiceSample || "").trim() && (
              <Box>
                <Typography sx={{ fontSize: 11, fontWeight: 600, color: "#475569", mb: 0.5 }}>Original Sample</Typography>
                <TextField
                  size="small"
                  fullWidth
                  multiline
                  rows={3}
                  value={draft.voiceSample}
                  onChange={(e) => update("voiceSample", e.target.value)}
                  placeholder="Paste an email you've written..."
                                   sx={{ "& .MuiInputBase-root": { fontSize: 12 } }}
                />
              </Box>
            )}

            {/* Additional samples */}
            {(draft.voiceSamples || []).map((sample, idx) => (
              <Box key={idx}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
                  <Typography sx={{ fontSize: 11, fontWeight: 600, color: "#475569" }}>
                    Sample {(draft.voiceSample?.trim() ? 2 : 1) + idx}
                  </Typography>
                  <IconButton size="small" onClick={() => removeSample(idx)} sx={{ color: "#9CA3AF", "&:hover": { color: "#6B7280", backgroundColor: "#F3F4F6" }, p: 0.25 }}>
                    <DeleteIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Box>
                <TextField
                  size="small"
                  fullWidth
                  multiline
                  rows={3}
                  value={sample}
                  onChange={(e) => updateSample(idx, e.target.value)}
                  placeholder="Paste another email you've written..."
                                   sx={{ "& .MuiInputBase-root": { fontSize: 12 } }}
                />
              </Box>
            ))}

            {/* First sample prompt if none exist */}
            {sampleCount === 0 && (
              <TextField
                size="small"
                fullWidth
                multiline
                rows={3}
                value={draft.voiceSample || ""}
                onChange={(e) => update("voiceSample", e.target.value)}
                placeholder="Paste an email you've written so the AI can learn your voice..."
                               sx={{ "& .MuiInputBase-root": { fontSize: 12 } }}
              />
            )}
          </Box>
        </TrainingCard>

        {/* ── 3. Custom Instructions ── */}
        <TrainingCard
          title="Custom Instructions"
          icon={<RulesIcon sx={{ fontSize: 16, color: "#059669" }} />}
          open={openCards.rules}
          onToggle={() => toggleCard("rules")}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Typography sx={{ fontSize: 11, color: "#64748b", lineHeight: 1.4 }}>
              Add specific rules the AI must follow when composing in this mode. These override default behavior.
            </Typography>
            <TextField
              size="small"
              fullWidth
              multiline
              rows={4}
              value={draft.customInstructions || ""}
              onChange={(e) => update("customInstructions", e.target.value)}
              placeholder={"e.g.\n- Never use exclamation marks\n- Always mention our product name\n- Keep subject lines under 6 words\n- Reference the recipient's company in the opening"}
                           sx={{ "& .MuiInputBase-root": { fontSize: 12 } }}
            />
          </Box>
        </TrainingCard>

        {/* ── 4. Prompt Preview (editable) ── */}
        <TrainingCard
          title="Final Prompt Preview"
          icon={<PreviewIcon sx={{ fontSize: 16, color: "#94a3b8" }} />}
          open={openCards.preview}
          onToggle={() => toggleCard("preview")}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Typography sx={{ fontSize: 11, color: "#64748b", lineHeight: 1.4 }}>
              This is the system prompt sent to the LLM. It's assembled from your settings above, but you can edit it directly here. Changes are saved with the mode.
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={12}
              value={editedPreview}
              onChange={(e) => {
                setEditedPreview(e.target.value);
                setDirty(true);
              }}
              placeholder="No prompt generated yet. Configure settings above."
              sx={{
                "& .MuiInputBase-root": {
                  backgroundColor: "#1e293b",
                  color: "#e2e8f0",
                  fontSize: 11,
                  fontFamily: "monospace",
                  lineHeight: 1.5,
                },
                "& .MuiInputBase-input": {
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                },
              }}
            />
          </Box>
        </TrainingCard>
      </Box>
    </MyMagicPanelShell>
  );
};

export default VoiceProfileDetailPanel;
